import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { orgUiPath } from '@/core/api';
import { useOrgId } from '@/hooks/useOrgId';
import { useFormats, useTemplateInFormat, useTemplatesForFormat } from '@/hooks/useTemplates';
import type { TemplateSummary } from '@/types/api';
import { creativeEditorLinkState } from '@/rve/editorBackNav';
import { NewCreativeModal } from '@/components/modals/NewCreativeModal';
import { TemplatePreview } from '@/components/app/TemplatePreview';
import {
  DESIGN_CANVAS_HEIGHT_PX,
  DESIGN_CANVAS_WIDTH_PX,
} from '@/creativeComposition/elementMerge';
import { CUSTOM_FORMAT_PATH_ID } from '@/services/formats';

const FORMAT_ICONS: Record<string, string> = {
  standard: 'rectangle',
  animation: 'movie',
  carousel: 'view_carousel',
  multi: 'groups',
  interaction: 'touch_app',
  ar: 'view_in_ar',
  custom: 'palette',
};

export default function FormatsPage() {
  const orgId = useOrgId();
  const [selectedFormatPathId, setSelectedFormatPathId] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isNewCreativeOpen, setIsNewCreativeOpen] = useState(false);
  const navigate = useNavigate();

  const { data: formats, loading: formatsLoading, error: formatsError } = useFormats();
  const formatList = useMemo(() => formats ?? [], [formats]);
  const activeFormatPathId = selectedFormatPathId ?? formatList[0]?.id ?? null;

  const { data: templates, loading: templatesLoading, error: templatesError } =
    useTemplatesForFormat(activeFormatPathId);
  const templateDetail = useTemplateInFormat(activeFormatPathId, selectedTemplateId);

  const allTemplates = useMemo(() => templates ?? [], [templates]);

  const selectedFormatMeta = useMemo(
    () => formatList.find((f) => f.id === activeFormatPathId) ?? null,
    [formatList, activeFormatPathId],
  );

  const selectedTemplate = useMemo(
    () => allTemplates.find((t) => t.id === selectedTemplateId) ?? null,
    [allTemplates, selectedTemplateId],
  );

  const previewWidth = Number(
    templateDetail.data?.render_settings?.width_px ?? DESIGN_CANVAS_WIDTH_PX,
  );
  const previewHeight = Number(
    templateDetail.data?.render_settings?.height_px ?? DESIGN_CANVAS_HEIGHT_PX,
  );
  const previewAspect =
    Number.isFinite(previewWidth) && Number.isFinite(previewHeight) && previewHeight > 0
      ? `${previewWidth} / ${previewHeight}`
      : `${DESIGN_CANVAS_WIDTH_PX} / ${DESIGN_CANVAS_HEIGHT_PX}`;

  function handleUseTemplate() {
    if (!selectedTemplate) return;
    setIsNewCreativeOpen(true);
  }

  const error = formatsError || templatesError;

  return (
    <div className="aspectly:p-4 aspectly:md:p-8 aspectly:flex aspectly:flex-col aspectly:gap-8 aspectly:min-h-[calc(100vh-4rem)]">
      <div className="aspectly:grid aspectly:grid-cols-1 aspectly:lg:grid-cols-12 aspectly:gap-8 aspectly:h-full">
        <div className="aspectly:col-span-1 aspectly:lg:col-span-5 aspectly:flex aspectly:flex-col aspectly:gap-6">
          <div className="aspectly:flex aspectly:flex-col aspectly:gap-3">
            <div className="aspectly:flex aspectly:items-center aspectly:justify-between">
              <p className="aspectly:text-[10px] aspectly:font-bold aspectly:text-on-surface-variant aspectly:uppercase aspectly:tracking-widest">
                Select Format
              </p>
            </div>
            {formatsLoading && (
              <div className="aspectly:flex aspectly:gap-2">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="aspectly:h-20 aspectly:w-20 aspectly:bg-surface-container-lowest aspectly:rounded-xl aspectly:animate-pulse"
                  />
                ))}
              </div>
            )}
            {!formatsLoading && formatList.length > 0 && (
              <div className="aspectly:flex aspectly:gap-2 aspectly:overflow-x-auto aspectly:pb-1 aspectly:no-scrollbar">
                {formatList.map((f) => {
                  const isSelected = activeFormatPathId === f.id;
                  const icon = FORMAT_ICONS[f.id] ?? 'view_quilt';
                  const isCustom = f.id === CUSTOM_FORMAT_PATH_ID;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => {
                        setSelectedFormatPathId(f.id);
                        setSelectedTemplateId(null);
                      }}
                      className={`aspectly:shrink-0 aspectly:flex aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:gap-2 aspectly:px-3 aspectly:py-3 aspectly:rounded-xl aspectly:min-w-[72px] aspectly:transition-transform aspectly:active:scale-95 ${
                        isSelected
                          ? 'aspectly:bg-primary aspectly:text-on-primary aspectly:shadow-md aspectly:shadow-primary/20' : 'aspectly:bg-surface-container-lowest aspectly:text-on-surface-variant aspectly:hover:bg-primary-container aspectly:transition-all'
                      }`}
                    >
                      <span className="material-symbols-outlined aspectly:text-xl">{icon}</span>
                      <span className="aspectly:text-[9px] aspectly:font-bold aspectly:uppercase aspectly:tracking-tight aspectly:text-center aspectly:leading-tight">
                        {f.name}
                      </span>
                      {isCustom && (
                        <span className="aspectly:text-[8px] aspectly:font-black aspectly:uppercase aspectly:tracking-tighter aspectly:opacity-80">
                          Custom
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="aspectly:flex aspectly:flex-col aspectly:gap-4 aspectly:mt-2">
            <div className="aspectly:flex aspectly:items-center aspectly:justify-between">
              <h2 className="aspectly:text-lg aspectly:font-bold aspectly:font-headline aspectly:text-on-surface">
                Templates: {selectedFormatMeta?.name ?? '—'}
              </h2>
              {!templatesLoading && (
                <span className="aspectly:text-xs aspectly:text-on-surface-variant">
                  {allTemplates.length} Variations
                </span>
              )}
            </div>

            {error && (
              <div className="aspectly:p-4 aspectly:rounded-xl aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
                {error}
              </div>
            )}

            {templatesLoading && (
              <div className="aspectly:space-y-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="aspectly:h-20 aspectly:bg-surface-container-lowest aspectly:rounded-xl aspectly:border aspectly:border-outline-variant/10 aspectly:animate-pulse"
                  />
                ))}
              </div>
            )}

            {!templatesLoading && allTemplates.length === 0 && (
              <div className="aspectly:py-8 aspectly:text-center aspectly:bg-surface-container-lowest aspectly:rounded-xl aspectly:border aspectly:border-outline-variant/10">
                <span className="material-symbols-outlined aspectly:text-4xl aspectly:text-primary/30">
                  grid_view
                </span>
                <p className="aspectly:mt-2 aspectly:text-sm aspectly:text-on-surface-variant">No templates in this format.</p>
              </div>
            )}

            {!templatesLoading && allTemplates.length > 0 && (
              <div className="aspectly:space-y-3">
                {allTemplates.map((t: TemplateSummary) => {
                  const isSelected = selectedTemplateId === t.id;
                  const isCustomTpl = t.format_id.startsWith('custom-');
                  return (
                    <div
                      key={t.id}
                      onClick={() => setSelectedTemplateId(t.id)}
                      className={`aspectly:p-4 aspectly:rounded-xl aspectly:flex aspectly:items-start aspectly:gap-4 aspectly:cursor-pointer aspectly:transition-all ${
                        isSelected
                          ? 'aspectly:bg-primary-container aspectly:border-l-4 aspectly:border-primary' : 'aspectly:bg-surface-container-lowest aspectly:hover:bg-primary-container aspectly:border-l-4 aspectly:border-transparent aspectly:hover:border-primary-container/30'
                      }`}
                    >
                      <div
                        className={`aspectly:w-10 aspectly:h-10 aspectly:rounded-lg aspectly:flex aspectly:items-center aspectly:justify-center aspectly:shrink-0 ${isSelected ? 'aspectly:bg-primary' : 'aspectly:bg-surface-container'}`}
                      >
                        <span
                          className={`material-symbols-outlined aspectly:text-xl ${isSelected ? 'aspectly:text-on-primary' : 'aspectly:text-on-surface-variant'}`}
                        >
                          tv
                        </span>
                      </div>
                      <div className="aspectly:flex-1 aspectly:min-w-0">
                        <div className="aspectly:flex aspectly:items-center aspectly:justify-between aspectly:gap-2">
                          <h3
                            className={`aspectly:font-bold aspectly:text-sm aspectly:truncate ${isSelected ? 'aspectly:text-on-primary-container' : 'aspectly:text-on-surface'}`}
                          >
                            {t.name}
                          </h3>
                          {isCustomTpl && (
                            <span className="aspectly:text-[9px] aspectly:uppercase aspectly:font-black aspectly:tracking-widest aspectly:text-primary aspectly:shrink-0">
                              Custom
                            </span>
                          )}
                        </div>
                        <p
                          className={`aspectly:text-xs aspectly:mt-1 aspectly:line-clamp-2 ${isSelected ? 'aspectly:text-on-primary-container/80' : 'aspectly:text-on-surface-variant'}`}
                        >
                          {t.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="aspectly:col-span-1 aspectly:lg:col-span-7 aspectly:flex aspectly:flex-col aspectly:gap-4">
          <div className="aspectly:bg-surface-container-lowest aspectly:rounded-2xl aspectly:p-6 aspectly:shadow-xs aspectly:flex aspectly:flex-col aspectly:relative aspectly:overflow-hidden aspectly:h-full aspectly:min-h-[500px]">
            {selectedTemplate ? (
              <>
                <div className="aspectly:flex aspectly:justify-between aspectly:items-center aspectly:mb-6 aspectly:shrink-0">
                  <div className="aspectly:flex aspectly:flex-col">
                    <h2 className="aspectly:text-xl aspectly:font-extrabold aspectly:font-headline aspectly:text-on-surface">
                      {selectedTemplate.name}
                    </h2>
                    <span className="aspectly:text-xs aspectly:text-on-surface-variant aspectly:font-medium aspectly:mt-1">
                      {selectedTemplate.description}
                    </span>
                  </div>
                </div>

                <div className="aspectly:relative aspectly:overflow-hidden aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-surface aspectly:rounded-xl aspectly:p-4 aspectly:grow aspectly:min-h-[300px]">
                  <div className="aspectly:relative aspectly:w-full aspectly:max-w-2xl aspectly:mx-auto aspectly:flex aspectly:flex-col aspectly:items-center">
                    <div
                      className="aspectly:relative aspectly:w-full aspectly:bg-[#0b0f14] aspectly:rounded-[1rem] aspectly:p-[1.5%] aspectly:shadow-2xl aspectly:border aspectly:border-black/60 aspectly:overflow-hidden"
                      style={{ aspectRatio: previewAspect }}
                    >
                      {templateDetail.loading && (
                        <div className="aspectly:w-full aspectly:h-full aspectly:animate-pulse aspectly:rounded-xs aspectly:bg-surface-container" />
                      )}
                      {!templateDetail.loading && templateDetail.error && (
                        <div className="aspectly:w-full aspectly:h-full aspectly:rounded-xs aspectly:bg-surface-container aspectly:flex aspectly:items-center aspectly:justify-center aspectly:text-xs aspectly:text-on-surface-variant">
                          Failed to load preview
                        </div>
                      )}
                      {!templateDetail.loading && !templateDetail.error && templateDetail.data && (
                        <TemplatePreview template={templateDetail.data} />
                      )}
                      {!templateDetail.loading && !templateDetail.data && !templateDetail.error && (
                        <div className="aspectly:w-full aspectly:h-full aspectly:bg-linear-to-br aspectly:from-primary-container aspectly:to-surface-container aspectly:rounded-xs aspectly:overflow-hidden aspectly:flex aspectly:items-center aspectly:justify-center">
                          <div className="aspectly:text-center aspectly:space-y-3 aspectly:opacity-40">
                            <span className="material-symbols-outlined aspectly:text-7xl aspectly:text-primary">
                              tv
                            </span>
                            <p className="aspectly:text-sm aspectly:font-bold aspectly:text-on-surface-variant">
                              {selectedTemplate.name}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="aspectly:flex aspectly:flex-col aspectly:items-center">
                      <div className="aspectly:w-24 aspectly:md:w-32 aspectly:h-2 aspectly:bg-[#0f141a] aspectly:rounded-b-lg" />
                      <div className="aspectly:w-36 aspectly:md:w-48 aspectly:h-1.5 aspectly:bg-[#11161d] aspectly:rounded-full aspectly:-mt-0.5 aspectly:shadow-lg" />
                    </div>
                  </div>
                </div>

                <div className="aspectly:grid aspectly:grid-cols-1 aspectly:md:grid-cols-2 aspectly:gap-6 aspectly:mt-6 aspectly:shrink-0">
                  <div className="aspectly:space-y-3">
                    <p className="aspectly:text-[11px] aspectly:font-bold aspectly:text-on-surface-variant aspectly:uppercase aspectly:tracking-widest">
                      Mandatory Elements
                    </p>
                    <div className="aspectly:flex aspectly:flex-wrap aspectly:gap-2">
                      <span className="aspectly:px-3 aspectly:py-1.5 aspectly:bg-primary-container aspectly:text-on-surface aspectly:text-[10px] aspectly:font-bold aspectly:rounded-lg aspectly:flex aspectly:items-center aspectly:gap-1.5">
                        <span className="material-symbols-outlined aspectly:text-[14px]">token</span> Logo
                      </span>
                      <span className="aspectly:px-3 aspectly:py-1.5 aspectly:bg-primary-container aspectly:text-on-surface aspectly:text-[10px] aspectly:font-bold aspectly:rounded-lg aspectly:flex aspectly:items-center aspectly:gap-1.5">
                        <span className="material-symbols-outlined aspectly:text-[14px]">videocam</span>{' '}
                        Vertical Video
                      </span>
                      <span className="aspectly:px-3 aspectly:py-1.5 aspectly:bg-primary-container aspectly:text-on-surface aspectly:text-[10px] aspectly:font-bold aspectly:rounded-lg aspectly:flex aspectly:items-center aspectly:gap-1.5">
                        <span className="material-symbols-outlined aspectly:text-[14px]">image</span> Product
                        Photo
                      </span>
                    </div>
                  </div>
                  <div className="aspectly:flex aspectly:justify-start aspectly:md:justify-end aspectly:items-end">
                    <button
                      type="button"
                      onClick={handleUseTemplate}
                      className="aspectly:bg-primary aspectly:text-on-primary aspectly:px-6 aspectly:md:px-8 aspectly:py-3 aspectly:md:py-4 aspectly:rounded-xl aspectly:font-bold aspectly:flex aspectly:items-center aspectly:justify-center aspectly:gap-3 aspectly:shadow-lg aspectly:shadow-primary/30 aspectly:hover:scale-[1.02] aspectly:active:scale-[0.98] aspectly:transition-all aspectly:w-full aspectly:md:w-auto"
                    >
                      <span className="material-symbols-outlined">edit_square</span> Use This
                      Template
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="aspectly:grow aspectly:flex aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:text-center aspectly:p-8 aspectly:opacity-60">
                <span className="material-symbols-outlined aspectly:text-6xl aspectly:text-on-surface-variant aspectly:mb-4">
                  view_in_ar
                </span>
                <h3 className="aspectly:text-lg aspectly:font-bold aspectly:text-on-surface aspectly:font-headline aspectly:mb-2">
                  Select a Template
                </h3>
                <p className="aspectly:text-sm aspectly:text-on-surface-variant aspectly:max-w-sm">
                  Choose a template from the list on the left to preview its format and mandatory
                  elements.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <NewCreativeModal
        isOpen={isNewCreativeOpen}
        onClose={() => setIsNewCreativeOpen(false)}
        initialTemplateId={selectedTemplateId}
        initialFormatId={activeFormatPathId}
        onCreated={(created) =>
          navigate(orgUiPath(orgId, `/creatives/${created.id}`), {
            state: creativeEditorLinkState('creatives'),
          })
        }
      />
    </div>
  );
}
